import {
  ChangeEvent,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { ERROR_CODE_ABORTED } from '~shared/api/upload-with-progress';
import { checkIsImageByFileName } from '~shared/lib/check-is-image';

import { Button } from '@breezeventures/ui-kit/button';
import { Icon } from '@breezeventures/ui-kit/next/icon';
import { Typography } from '@breezeventures/ui-kit/typography';

import { FileRow } from './file-row';
import { mockUpload } from './lib/mock-upload';
import { validateFile } from './lib/validate-file';
import { FileUploadOptions, PreloadedFile, FileToUpload } from './types';

const generateFileId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const createErrorFile = (file: File, error?: string): FileToUpload => ({
  id: generateFileId(),
  file,
  status: 'error',
  error,
});

const createPreview = (file: File) => {
  if (!file.type.startsWith('image/')) return;

  return URL.createObjectURL(file);
};

const createUploadingFile = (file: File, id: string): FileToUpload => ({
  id,
  file,
  previewUrl: createPreview(file),
  progress: 0,
  status: 'uploading',
});

interface FileUploadProps {
  title: string;
  description?: string;
  options?: FileUploadOptions;
  preloadedFiles?: PreloadedFile[];
  onFileRemove?: (fileId: string) => void;
  onUpload?: (
    file: { fileUrl?: string; file: File },
    onProgress: (progress: number) => void,
    signal?: AbortSignal
  ) => Promise<{ documentId: string }>;
}

export type FileUploadRef = {
  checkIsUploading: () => boolean;
};

export const FileUpload = forwardRef<FileUploadRef, FileUploadProps>(
  (props, ref) => {
    const {
      title,
      description,
      options = {},
      preloadedFiles = [],
      onFileRemove,
      onUpload,
    } = props;
    const { multiple = false, showProgress = true, capture } = options;

    const [filesToUpload, setFilesToUpload] = useState<FileToUpload[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

    useImperativeHandle(ref, () => {
      return {
        checkIsUploading() {
          return filesToUpload.some((file) => file.status === 'uploading');
        },
      };
    }, [filesToUpload]);

    const handleFileSelect = useCallback(
      async (fileList: FileList | null) => {
        if (!fileList?.length) return;

        const selectedFiles = [...fileList];

        const nextFiles: FileToUpload[] = [];

        for (const file of selectedFiles) {
          const validation = validateFile(file, options);

          if (!validation.valid) {
            nextFiles.push(createErrorFile(file, validation.error));
            continue;
          }

          const fileId = generateFileId();
          const uploadingFile = createUploadingFile(file, fileId);

          nextFiles.push(uploadingFile);

          const controller = new AbortController();
          abortControllersRef.current.set(fileId, controller);

          const uploadFn = onUpload ?? mockUpload;

          uploadFn(
            { file, fileUrl: uploadingFile.previewUrl },
            (progress) => {
              setFilesToUpload((prev) =>
                prev.map((file) =>
                  file.id === fileId
                    ? {
                        ...file,
                        progress,
                        status: 'uploading',
                      }
                    : file
                )
              );
            },
            controller.signal
          )
            .then(() => {
              setFilesToUpload((prev) =>
                prev.filter((file) => file.id !== fileId)
              );
              abortControllersRef.current.delete(fileId);
            })
            .catch((error) => {
              abortControllersRef.current.delete(fileId);

              const isAborted =
                (error as Error)?.message === ERROR_CODE_ABORTED;

              if (!isAborted) {
                const message = (error as Error)?.message ?? 'Upload failed';
                setFilesToUpload((prev) =>
                  prev.map((file) =>
                    file.id === fileId
                      ? { ...file, status: 'error', error: message }
                      : file
                  )
                );
              }
            });
        }

        setFilesToUpload((prev) =>
          multiple ? [...prev, ...nextFiles] : nextFiles
        );
      },
      [options, onUpload, multiple]
    );

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      handleFileSelect(event.target.files);
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveFileToUpload = (id: string) => {
      const controller = abortControllersRef.current.get(id);

      const fileToRemove = filesToUpload.find((file) => file.id === id);

      if (fileToRemove?.previewUrl) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }

      setFilesToUpload((prev) => prev.filter((file) => file.id !== id));

      if (controller && fileToRemove?.status === 'uploading') {
        controller.abort();
        abortControllersRef.current.delete(id);
        return;
      }
    };

    const hasFiles = preloadedFiles.length > 0 || filesToUpload.length > 0;
    const showUploadButton = !hasFiles || multiple;

    return (
      <div
        className={`
          flex w-full flex-col gap-3 rounded-2xl border border-neutral-100
          bg-white p-4 shadow-lg
        `}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <Typography.Text size="m" weight="medium">
              {title}
            </Typography.Text>
            {description && (
              <Typography.Text size="xs" color="secondary">
                {description}
              </Typography.Text>
            )}
          </div>

          {showUploadButton && (
            <Button
              type="button"
              size="s"
              className="size-10! shrink-0 p-0!"
              aria-label={`Upload ${title}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="plus" size={24} version="v1" className="size-6" />
            </Button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          aria-label={title}
          multiple={multiple}
          onChange={handleInputChange}
          className="hidden"
          accept={options.allowedExtensions?.map((ext) => `.${ext}`).join(',')}
          capture={capture}
        />

        {hasFiles && (
          <div className="flex flex-col gap-4">
            {preloadedFiles.map((file) => {
              const isImage = checkIsImageByFileName(file.fileName);

              return (
                <FileRow
                  key={file.id}
                  name={file.fileName}
                  isImage={isImage}
                  previewUrl={isImage ? file.fileUrl : undefined}
                  onRemove={() => onFileRemove?.(file.id)}
                />
              );
            })}

            {filesToUpload.map((file) => (
              <FileRow
                key={file.id}
                name={file.file.name}
                isImage={file.file.type.startsWith('image/')}
                previewUrl={file.previewUrl}
                error={file.status === 'error' ? file.error : ''}
                uploadingState={
                  file.status === 'uploading'
                    ? showProgress
                      ? { progress: file.progress }
                      : { indeterminate: true }
                    : undefined
                }
                onRemove={() => handleRemoveFileToUpload(file.id)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
);
FileUpload.displayName = 'FileUpload';
